import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import PoolIcon from '@mui/icons-material/Pool';
import GroupsIcon from '@mui/icons-material/Groups';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';
import swimService from '../../api/services/swimService';
import programService from '../../api/services/programService';
import { CoachCalendarDay } from '../../types/swim';
import { Program } from '../../types';

const todayIso = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const addDaysIso = (iso: string, days: number): string => {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, (m ?? 1) - 1, d ?? 1);
  dt.setDate(dt.getDate() + days);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

const formatShortDate = (iso: string): string => {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, (m ?? 1) - 1, d ?? 1);
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

interface MetricRowProps {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  emphasis?: 'default' | 'warning' | 'success';
}

const MetricRow: React.FC<MetricRowProps> = ({ icon, label, value, emphasis = 'default' }) => {
  const color =
    emphasis === 'warning' ? 'warning.main' : emphasis === 'success' ? 'success.main' : 'text.primary';
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>{icon}</Box>
      <Typography variant="body2" sx={{ flex: 1 }}>
        {label}
      </Typography>
      <Typography variant="h6" sx={{ color, lineHeight: 1, fontWeight: 600 }}>
        {value}
      </Typography>
    </Stack>
  );
};

const SwimTodayCard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [todaySession, setTodaySession] = useState<CoachCalendarDay | null>(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [endingPrograms, setEndingPrograms] = useState<Program[]>([]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      const today = todayIso();
      const horizon = addDaysIso(today, 6);

      const [calRes, pendRes, progRes] = await Promise.allSettled([
        swimService.coachCalendar(today, today),
        swimService.listPending(),
        programService.getAllPrograms(false),
      ]);
      if (cancelled) return;

      if (calRes.status === 'fulfilled') {
        setTodaySession(calRes.value.data[0] ?? null);
      }
      if (pendRes.status === 'fulfilled') {
        setPendingCount(pendRes.value.length);
      }
      if (progRes.status === 'fulfilled') {
        const ending = progRes.value
          .filter((p) => p.program_type === 'swimming')
          .filter((p) => p.status === 'published')
          .filter((p) => p.end_date >= today && p.end_date <= horizon)
          .sort((a, b) => a.end_date.localeCompare(b.end_date));
        setEndingPrograms(ending);
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const todaySessionCount = useMemo(
    () => (todaySession?.session_count ?? 0) - (todaySession?.cancelled_session_count ?? 0),
    [todaySession],
  );
  const todayCompetitionCount = todaySession?.competitions?.length ?? 0;
  const todayLessonCount = todaySession?.lessons?.length ?? 0;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PoolIcon color="primary" />
          Swim — Today
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack spacing={1.25}>
            <MetricRow
              icon={<GroupsIcon fontSize="small" />}
              label="Sessions today"
              value={todaySessionCount}
            />
            {todayCompetitionCount > 0 && (
              <MetricRow
                icon={<EmojiEventsIcon fontSize="small" />}
                label="Meets today"
                value={todayCompetitionCount}
                emphasis="success"
              />
            )}
            {todayLessonCount > 0 && (
              <MetricRow
                icon={<PoolIcon fontSize="small" />}
                label="Private lessons today"
                value={todayLessonCount}
              />
            )}
            <MetricRow
              icon={<HourglassEmptyIcon fontSize="small" />}
              label="Pending enrollments"
              value={pendingCount}
              emphasis={pendingCount > 0 ? 'warning' : 'default'}
            />
            <MetricRow
              icon={<EventBusyIcon fontSize="small" />}
              label="Programs ending in 7 days"
              value={endingPrograms.length}
              emphasis={endingPrograms.length > 0 ? 'warning' : 'default'}
            />
            {endingPrograms.length > 0 && (
              <Box sx={{ pl: 4 }}>
                {endingPrograms.slice(0, 3).map((p) => (
                  <Typography key={p.id} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {p.name} — ends {formatShortDate(p.end_date)}
                  </Typography>
                ))}
              </Box>
            )}

            <Box
              sx={{
                pt: 0.5,
                display: 'grid',
                gap: 1,
                // Auto-fit wraps the buttons onto more rows instead of squeezing them
                // (which overflowed the labels on desktop). Single column on mobile.
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(140px, 1fr))' },
              }}
            >
              <Button
                variant="outlined"
                size="small"
                endIcon={<ChevronRightIcon />}
                onClick={() => navigate('/coach/schedule')}
                fullWidth
              >
                Coach Schedule
              </Button>
              <Button
                variant="outlined"
                size="small"
                endIcon={<ChevronRightIcon />}
                onClick={() => navigate('/admin/swim/pending')}
                fullWidth
              >
                Confirm Sign-ups
              </Button>
              <Button
                variant="outlined"
                size="small"
                endIcon={<ChevronRightIcon />}
                onClick={() => navigate('/admin/swim/cancellations')}
                fullWidth
              >
                Cancellations
              </Button>
              <Button
                variant="outlined"
                size="small"
                endIcon={<ChevronRightIcon />}
                onClick={() => navigate('/admin/swim/programs')}
                fullWidth
              >
                Programs
              </Button>
            </Box>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default SwimTodayCard;
